import React, { useState, useEffect } from "react";
import { NavbarIcon, LeftSidebarIcon, RightSidebarIcon } from "./icons";
import { API_ENDPOINTS } from "./constants";
import { apiFetch } from "../../core/utils/api";
import { normalizeSubdomain } from "../../core/utils/subdomainUtils";
import { typography, surfaces, borders, textColors, brandColors, interactive } from "../../design-system/tokens";

const CreatePageForm = ({ 
  createPageData, 
  setCreatePageData, 
  createPageError, 
  setCreatePageError,
  isCreatingPage,
  hasNavbar,
  hasLeftSidebar,
  hasRightSidebar,
  onSubmit,
  onCancel
}) => {
  const [navigationExpanded, setNavigationExpanded] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [subdomainManuallyEdited, setSubdomainManuallyEdited] = useState(false);

  const handleNameChange = (e) => {
    const nextName = e.target.value;
    setCreatePageData((prev) => {
      const next = { ...prev, name: nextName };
      // Auto-generate subdomain only while the user hasn't manually edited it.
      if (!subdomainManuallyEdited) {
        next.subdomain = normalizeSubdomain(nextName);
      }
      return next;
    });
  };

  const handleSubdomainChange = (e) => {
    setSubdomainManuallyEdited(true);
    // Allow user to type any characters; normalize later (on blur / submit).
    setCreatePageData((prev) => ({ ...prev, subdomain: e.target.value }));
  };

  const handleSubdomainBlur = () => {
    // Normalize after typing so the field ends up URL-safe.
    setCreatePageData((prev) => ({ ...prev, subdomain: normalizeSubdomain(prev.subdomain) }));
  };

  // Initialize form with proper defaults based on existing navigation
  useEffect(() => {
    setCreatePageData(prev => ({
      ...prev,
      // For existing navigation, default to adding to them
      addToNavbar: hasNavbar,
      addToLeftSidebar: hasLeftSidebar,
      addToRightSidebar: hasRightSidebar,
      // For non-existing navigation, default to not creating them (except navbar)
      createNavbar: !hasNavbar,
      createLeftSidebar: false,
      createRightSidebar: false
    }));
  }, [hasNavbar, hasLeftSidebar, hasRightSidebar, setCreatePageData]);

  // Fetch page templates list
  useEffect(() => {
    let mounted = true;
    const run = async () => {
      setLoadingTemplates(true);
      try {
        const res = await apiFetch(API_ENDPOINTS.PAGE_TEMPLATES(), { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (mounted) {
          setTemplates(data);
          // Select first option by default if none selected
          if (data && data.length && !createPageData.template) {
            setCreatePageData(prev => ({ ...prev, template: data[0].value }));
          }
        }
      } finally {
        if (mounted) setLoadingTemplates(false);
      }
    };
    run();
    return () => { mounted = false; };
  }, [setCreatePageData, createPageData.template]);

  return (
    <form onSubmit={onSubmit} className="p-4">
      <h3 className={`${typography.h4} ${textColors.primary} mb-4`}>Create New Page</h3>
      
      {createPageError && (
        <div className={`mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md`}>
          <p className={`${typography.bodySmall} text-red-600 dark:text-red-400`}>{createPageError}</p>
        </div>
      )}
      
      <div className="space-y-6">
        <div className="space-y-4">
          <h4 className={`${typography.h5} ${textColors.primary} border-b ${borders.default} pb-2`}>Page Details</h4>
          
          <div>
            <label htmlFor="create-page-name-input" className={`${typography.label} ${textColors.primary} mb-1`}>
              Page Name
            </label>
            <input
              id="create-page-name-input"
              type="text"
              value={createPageData.name}
              onChange={handleNameChange}
              className={`w-full px-3 py-2 border ${borders.default} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500 focus:border-transparent ${surfaces.primary} ${textColors.primary}`}
              placeholder="About Us"
              required
            />
          </div>

          {/* Subdomain moved above template */}
          <div>
            <label htmlFor="create-page-subdomain-input" className={`${typography.label} ${textColors.primary} mb-1`}>
              Subdomain
            </label>
            <p className={`${typography.caption} ${textColors.secondary} mb-2`}>
              If left empty, this becomes the main page of your website
            </p>
            <input
              id="create-page-subdomain-input"
              type="text"
              value={createPageData.subdomain}
              onChange={handleSubdomainChange}
              onBlur={handleSubdomainBlur}
              className={`w-full px-3 py-2 border ${borders.default} rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500 focus:border-transparent ${surfaces.primary} ${textColors.primary}`}
              placeholder="about"
            />
          </div>

          <div>
            <label className={`${typography.label} ${textColors.primary} mb-2`}>
              Page Template
            </label>
            {loadingTemplates ? (
              <div className={`${typography.bodySmall} ${textColors.secondary}`}>Loading templates...</div>
            ) : (
              <div className="grid grid-cols-1 gap-3 max-h-64 overflow-y-auto pr-1">
                {templates.map((t) => {
                  const selected = createPageData.template === t.value;
                  const initial = (t.name && t.name[0]) ? t.name[0].toUpperCase() : '?';
                  return (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setCreatePageData(prev => ({ ...prev, template: t.value }))}
                      className={`group text-left p-3 rounded-lg border transition-colors flex items-center ${selected ? 'border-blue-500 dark:border-blue-500 ring-2 ring-blue-200 dark:ring-blue-200 bg-blue-50 dark:bg-blue-900/20' : `border-gray-200 dark:border-gray-700 ${surfaces.primary} hover:border-blue-300 dark:hover:border-blue-300 ${surfaces.hover}`}`}
                    >
                      <div className={`flex items-center justify-center mr-3 w-9 h-9 rounded-full text-sm font-semibold ${selected ? 'bg-blue-600 dark:bg-blue-600 text-white' : `bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300`}`}>{initial}</div>
                      <div className="flex-1">
                        <div className={`${typography.body} ${textColors.primary} font-medium`}>{t.name}</div>
                        <div className={`${typography.caption} ${textColors.secondary} mt-1 opacity-0 max-h-0 group-hover:opacity-100 group-hover:max-h-20 transition-all duration-150 ease-out`}>{t.description}</div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <button
            type="button"
            onClick={() => setNavigationExpanded(!navigationExpanded)}
            className={`flex items-center justify-between w-full text-left border-b ${borders.default} pb-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors`}
          >
            <div className="flex items-center">
              <div className="mr-2 text-blue-600 dark:text-blue-400">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="w-5 h-5"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <line x1="9" y1="3" x2="9" y2="21" />
                </svg>
              </div>
              <h4 className={`${typography.h5} ${textColors.primary}`}>Navigation Configuration</h4>
            </div>
            <svg
              className={`w-5 h-5 ${textColors.secondary} transition-transform duration-200 ${
                navigationExpanded ? 'rotate-180' : ''
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {navigationExpanded && (
            <div className="grid grid-cols-1 gap-3">
              <div className={`flex items-center p-3 border ${borders.default} rounded-lg ${surfaces.hover} transition-colors`}>
                <div className="flex items-center flex-grow">
                  <div className="mr-3 text-blue-600 dark:text-blue-400">
                    <NavbarIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <span className={`${typography.bodySmall} ${textColors.primary}`}>
                      {!hasNavbar ? 'Add to New Navbar' : 'Add to Navbar'}
                    </span>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!hasNavbar ? createPageData.createNavbar : createPageData.addToNavbar}
                    onChange={(e) => setCreatePageData(prev => ({ 
                      ...prev, 
                      [!hasNavbar ? 'createNavbar' : 'addToNavbar']: e.target.checked 
                    }))}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className={`flex items-center p-3 border ${borders.default} rounded-lg ${surfaces.hover} transition-colors`}>
                <div className="flex items-center flex-grow">
                  <div className="mr-3 text-blue-600 dark:text-blue-400">
                    <LeftSidebarIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <span className={`${typography.bodySmall} ${textColors.primary}`}>
                      {!hasLeftSidebar ? 'Add to New Left Sidebar' : 'Add to Left Sidebar'}
                    </span>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!hasLeftSidebar ? createPageData.createLeftSidebar : createPageData.addToLeftSidebar}
                    onChange={(e) => setCreatePageData(prev => ({ 
                      ...prev, 
                      [!hasLeftSidebar ? 'createLeftSidebar' : 'addToLeftSidebar']: e.target.checked 
                    }))}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                </label>
              </div>

              <div className={`flex items-center p-3 border ${borders.default} rounded-lg ${surfaces.hover} transition-colors`}>
                <div className="flex items-center flex-grow">
                  <div className="mr-3 text-blue-600 dark:text-blue-400">
                    <RightSidebarIcon className="w-5 h-5" />
                  </div>
                  <div>
                    <span className={`${typography.bodySmall} ${textColors.primary}`}>
                      {!hasRightSidebar ? 'Add to New Right Sidebar' : 'Add to Right Sidebar'}
                    </span>
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!hasRightSidebar ? createPageData.createRightSidebar : createPageData.addToRightSidebar}
                    onChange={(e) => setCreatePageData(prev => ({ 
                      ...prev, 
                      [!hasRightSidebar ? 'createRightSidebar' : 'addToRightSidebar']: e.target.checked 
                    }))}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 dark:peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          )}
        </div>
      </div>
      
      <div className="flex justify-end space-x-3 mt-6">
        <button
          type="button"
          onClick={onCancel}
          className={`px-4 py-2 ${typography.button} ${textColors.secondary} ${textColors.hover}`}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={isCreatingPage}
          className={`px-4 py-2 ${interactive.primaryBg} ${interactive.primaryText} ${typography.button} rounded-md ${interactive.primaryHover} focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed`}
        >
          {isCreatingPage ? 'Creating...' : 'Create Page'}
        </button>
      </div>
    </form>
  );
};

export default CreatePageForm; 